import React, { useRef, useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";

import {
  Mic,
  Video,
  Paperclip,
  MicIcon,
  MessageSquareMore,
  VideoIcon,
  MessageSquareDot,
  HardDrive,
  Link,
  FolderOpen,
} from "lucide-react";
import { cn } from "@pineseed/gui/util";

/**
 * Input Action Icons Component
 * STRICT ARCHITECTURE COMPLIANCE: Pure UI only
 * - No business logic
 * - No external system calls
 * - Only renders UI based on state from props
 * - Triggers actions via handler callbacks
 */

interface InputActionIconsProps {
  className?: string;
  disabled?: boolean;
  hoverDelay?: number;
  isConnected?: boolean;
  isAuthenticated?: boolean;
  portalContainer?: HTMLElement | null;
  activePopup?: "microphone" | "camera" | "attachment" | null;
  onSetActivePopup?: (popup: "microphone" | "camera" | "attachment" | null) => void;
  canUseMicrophone?: boolean;
  canUseCamera?: boolean;
  onRecordAudio?: () => void;
  onVoiceChat?: () => void;
  onRecordVideo?: () => void;
  onVideoChat?: () => void;
  onAttachDevice?: () => void;
  onAttachConnection?: () => void;
  onAttachWorkspace?: () => void;
}

interface PopupOption {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  disabled: boolean;
  disabledReason?: "permissions" | "connection" | "implementation" | "auth";
  onClick: (event: React.MouseEvent) => void;
}

interface IconComponentProps {
  type: "microphone" | "camera" | "attachment";
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  disabled: boolean;
  activePopup: string | null;
  onHoverEnter: (type: "microphone" | "camera" | "attachment") => void;
  onHoverLeave: () => void;
  renderPopup: (
    type: "microphone" | "camera" | "attachment",
  ) => React.ReactNode;
}

function IconComponent({
  type,
  icon: IconElement,
  title,
  disabled,
  activePopup,
  onHoverEnter,
  onHoverLeave,
  renderPopup,
}: IconComponentProps) {
  return (
    <div
      className="relative"
      onMouseEnter={() => onHoverEnter(type)}
      onMouseLeave={onHoverLeave}
    >
      <button
        data-icon={type}
        disabled={disabled}
        title={title}
        className={cn(
          "p-1.5 rounded-md transition-colors relative z-10 bg-popover",
          disabled
            ? "text-muted-foreground cursor-not-allowed opacity-50"
            : "text-muted-foreground hover:text-foreground hover:bg-muted",
          activePopup === type && "bg-muted text-foreground",
        )}
      >
        <IconElement className="w-4 h-4" />
      </button>
      {renderPopup(type)}
    </div>
  );
}

const noop = () => {};

export function InputActionIcons({
  className = "",
  disabled = false,
  hoverDelay = 0,
  isConnected = false,
  isAuthenticated = false,
  portalContainer = null,
  activePopup: activePopupProp,
  onSetActivePopup: onSetActivePopupProp,
  canUseMicrophone = false,
  canUseCamera = false,
  onRecordAudio = noop,
  onVoiceChat = noop,
  onRecordVideo = noop,
  onVideoChat = noop,
  onAttachDevice = noop,
  onAttachConnection = noop,
  onAttachWorkspace = noop,
}: InputActionIconsProps) {
  // Internal popup state when caller doesn't manage it
  const [internalActivePopup, setInternalActivePopup] = useState<"microphone" | "camera" | "attachment" | null>(null);
  const activePopup = activePopupProp ?? internalActivePopup;
  const onSetActivePopup = onSetActivePopupProp ?? setInternalActivePopup;

  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRefHover = useRef<NodeJS.Timeout | null>(null);
  const timeoutRefHoverLeave = useRef<NodeJS.Timeout | null>(null);

  // Single envelope state - only one envelope exists at a time
  const [envelopeStyle, setEnvelopeStyle] =
    useState<React.CSSProperties | null>(null);

  // Popup position for portal rendering
  const [popupPosition, setPopupPosition] = useState<{
    left: number;
    bottom: number;
  } | null>(null);

  // Envelope lifecycle tied to popup lifecycle
  useEffect(() => {
    if (!activePopup) {
      // No active popup - cleanup envelope and popup position
      setEnvelopeStyle(null);
      setPopupPosition(null);
      return;
    }

    // Active popup - calculate envelope and popup position after DOM update
    const calculatePositions = () => {
      const container = containerRef.current;
      if (!container) return;

      // Find the icon button
      const iconButton = container.querySelector(
        `[data-icon="${activePopup}"]`,
      ) as HTMLElement;

      if (!iconButton) return;

      // Get bounding rectangles
      const iconRect = iconButton.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const portalContainerRect = portalContainer
        ? portalContainer.getBoundingClientRect()
        : null;

      // // Calculate popup position relative to viewport for portal
      // const newPopupPosition = {
      //   left: (portalContainer ? 0 - containerRect.left : 0) + iconRect.left,
      //   bottom: (portalContainer ? portalContainer.getBoundingClientRect().bottom : window.innerHeight) - iconRect.top + 8, // 8px gap above icon
      // } as {
      //   left: number;
      //   bottom: number;
      // };

      // For envelope calculation, we need to estimate popup dimensions
      // Popup is min-width 160px, positioned above icon with 8px margin
      const estimatedPopupWidth = 160;
      const estimatedPopupHeight = 80; // Estimate based on 2-3 options

      const popupLeft = iconRect.left;
      const popupRight = iconRect.left + estimatedPopupWidth;
      const popupBottom = iconRect.top - 8;
      const popupTop = popupBottom - estimatedPopupHeight;

      // Calculate envelope that encompasses both icon and estimated popup with 2px padding
      const minLeft =
        Math.min(iconRect.left, popupLeft) - containerRect.left - 2;
      const maxRight =
        Math.max(iconRect.right, popupRight) - containerRect.left + 2;
      const minTop = Math.min(iconRect.top, popupTop) - containerRect.top - 2;
      const maxBottom =
        Math.max(iconRect.bottom, iconRect.bottom) - containerRect.top + 2;

      const newEnvelopeStyle: React.CSSProperties = {
        position: "absolute",
        left: `${minLeft}px`,
        right: `${containerRect.width - maxRight}px`,
        top: `${minTop}px`,
        bottom: `${containerRect.height - maxBottom}px`,
        pointerEvents: "auto", // CRITICAL: Must be "auto" to receive mouse events
        zIndex: 1,
        // border: "1px dotted black",
      };

      // Calculate popup position
      const newPopupPosition = {
        left: portalContainerRect
          ? iconRect.right - iconRect.left + minLeft
          : containerRect.left + minLeft + 2,
        bottom:
          (portalContainerRect
            ? portalContainerRect.bottom
            : window.innerHeight) -
          iconRect.top +
          8, // 8px gap above icon
      } as {
        left: number;
        bottom: number;
      };

      // console.log("Positions calculated:", {
      //   type: activePopup,
      //   iconRect,
      //   popupPosition: newPopupPosition,
      //   envelope: newEnvelopeStyle,
      // });

      setEnvelopeStyle(newEnvelopeStyle);
      setPopupPosition(newPopupPosition);
    };

    // Compliant: rAF used **only** to wait one frame for DOM to render before measuring (no animation)
    requestAnimationFrame(calculatePositions);
  }, [activePopup]); // Tied to popup lifecycle

  // No click outside handling needed for hover-based popups

  // Handle hover zone events - envelope lifecycle is automatic via useEffect
  const handleHoverZoneEnter = useCallback(
    (type: "microphone" | "camera" | "attachment") => {
      if (disabled) return;
      // if (timeoutRefHoverLeave.current) {
      //   console.log("clear timeoutRefHoverLeave");
      //   clearTimeout(timeoutRefHoverLeave.current);
      //   timeoutRefHoverLeave.current = null;
      //   setActivePopup(type);
      // } else
      if (hoverDelay > 0 && !activePopup) {
        timeoutRefHover.current = setTimeout(() => {
          onSetActivePopup(type);
          timeoutRefHover.current = null;
        }, hoverDelay);
      } else {
        onSetActivePopup(type);
      }
    },
    [disabled, hoverDelay, activePopup, onSetActivePopup],
  );

  const handleHoverZoneLeave = useCallback(() => {
    // Close popup - envelope cleaned up automatically
    if (timeoutRefHover.current) {
      console.log("clear timeoutRefHover");
      clearTimeout(timeoutRefHover.current);
      // setActivePopup(null);
      timeoutRefHover.current = null;
    }
    // else if (hoverDelay > 0) {
    //   timeoutRefHoverLeave.current = setTimeout(() => {
    //     setActivePopup(null);
    //     timeoutRefHoverLeave.current = null;
    //   }, hoverDelay);
    // } else {
    onSetActivePopup(null);
    // }
  }, [timeoutRefHover, onSetActivePopup]);

  // Handle popup option clicks - pure UI only, trigger callbacks
  const handleOptionClick = (optionId: string, _event: React.MouseEvent) => {
    onSetActivePopup(null); // Close popup - pure UI state change only

    // console.log("handleOptionClick", optionId, event.target);

    // Trigger appropriate callback - NO business logic, NO async
    switch (optionId) {
      case "record-audio":
        onRecordAudio();
        break;
      case "voice-chat":
        onVoiceChat();
        break;
      case "record-video":
        onRecordVideo();
        break;
      case "video-chat":
        onVideoChat();
        break;
      case "attach-device":
        onAttachDevice();
        break;
      case "attach-connection":
        onAttachConnection();
        break;
      case "attach-workspace":
        onAttachWorkspace();
        break;
    }
  };

  // Define popup options for each icon type
  const getPopupOptions = (
    type: "microphone" | "camera" | "attachment",
  ): PopupOption[] => {
    switch (type) {
      case "microphone":
        return [
          {
            id: "record-audio",
            icon: MicIcon,
            label: "Record audio",
            disabled: !canUseMicrophone || !isConnected || true, // Still disabled pending implementation
            disabledReason: !isConnected
              ? "connection"
              : !canUseMicrophone
                ? "permissions"
                : "implementation",
            onClick: (e) => handleOptionClick("record-audio", e),
          },
          {
            id: "voice-chat",
            icon: MessageSquareMore,
            label: "Voice chat",
            disabled: !canUseMicrophone || !isConnected || true, // Still disabled pending implementation
            disabledReason: !isConnected
              ? "connection"
              : !canUseMicrophone
                ? "permissions"
                : "implementation",
            onClick: (e) => handleOptionClick("voice-chat", e),
          },
        ];
      case "camera":
        return [
          {
            id: "record-video",
            icon: VideoIcon,
            label: "Record video",
            disabled: !canUseCamera || !isConnected || true, // Still disabled pending implementation
            disabledReason: !isConnected
              ? "connection"
              : !canUseCamera
                ? "permissions"
                : "implementation",
            onClick: (e) => handleOptionClick("record-video", e),
          },
          {
            id: "video-chat",
            icon: MessageSquareDot,
            label: "Video chat",
            disabled: !canUseCamera || !isConnected || true, // Still disabled pending implementation
            disabledReason: !isConnected
              ? "connection"
              : !canUseCamera
                ? "permissions"
                : "implementation",
            onClick: (e) => handleOptionClick("video-chat", e),
          },
        ];
      case "attachment":
        return [
          {
            id: "attach-device",
            icon: HardDrive,
            label: "From device",
            disabled: !isConnected || !isAuthenticated,
            disabledReason: !isConnected
              ? "connection"
              : !isAuthenticated
                ? "auth"
                : undefined,
            onClick: (e) => handleOptionClick("attach-device", e),
          },
          {
            id: "attach-connection",
            icon: Link,
            label: "From connection",
            disabled: !isConnected || true, // Still disabled pending implementation
            disabledReason: !isConnected ? "connection" : "implementation",
            onClick: (e) => handleOptionClick("attach-connection", e),
          },
          {
            id: "attach-workspace",
            icon: FolderOpen,
            label: "From workspace",
            disabled: !isConnected || true, // Still disabled pending implementation
            disabledReason: !isConnected ? "connection" : "implementation",
            onClick: (e) => handleOptionClick("attach-workspace", e),
          },
        ];
      default:
        return [];
    }
  };

  // Render popup for active icon - portaled to document.body
  const renderPopup = (type: "microphone" | "camera" | "attachment") => {
    if (activePopup !== type || !popupPosition) return null;

    const options = getPopupOptions(type);

    const popupContent = (
      <div
        ref={(el) => {
          if (el) {
            // Add native event listener to stop propagation before it reaches document
            const stopPropagation = (e: Event) => e.stopPropagation();
            el.addEventListener("mousedown", stopPropagation, true); // capture phase
            return () =>
              el.removeEventListener("mousedown", stopPropagation, true);
          }
        }}
        data-popup={type}
        className="absolute bg-popover border border-border rounded-md shadow-lg min-w-[160px] overflow-hidden"
        style={{
          left: `${popupPosition.left}px`,
          bottom: `${popupPosition.bottom}px`,
          zIndex: 9999, // N/A for absolute positioning
          backdropFilter: "blur(10px)",
        }}
        // onMouseEnter={() => handleHoverZoneEnter(type)}
        onMouseLeave={handleHoverZoneLeave}
      >
        {options.map((option) => {
          const IconComponent = option.icon;
          const isConnectionDisabled = option.disabledReason === "connection";

          return (
            <button
              key={option.id}
              onClick={option.onClick}
              disabled={option.disabled}
              className={cn(
                "w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors",
                option.disabled
                  ? "text-muted-foreground cursor-not-allowed opacity-50"
                  : "text-foreground hover:bg-muted",
              )}
            >
              <IconComponent
                className={cn(
                  "w-4 h-4",
                  isConnectionDisabled && "line-through",
                )}
              />
              <span className={cn(isConnectionDisabled && "line-through")}>
                {option.label}
              </span>
            </button>
          );
        })}
      </div>
    );

    // Portal the popup to custom container or document.body to escape stacking context
    return createPortal(popupContent, portalContainer || document.body);
  };

  return (
    <div ref={containerRef} className={cn("", className)}>
      <div className={cn("flex items-center gap-2 relative h-full", "")}>
        {/* Single hover envelope that spans across icon and popup */}
        {envelopeStyle && (
          <div
            style={envelopeStyle}
            onMouseEnter={() =>
              activePopup && handleHoverZoneEnter(activePopup)
            }
            onMouseLeave={handleHoverZoneLeave}
          />
        )}

        {/* Microphone Icon */}
        <IconComponent
          type="microphone"
          icon={Mic}
          title="Audio options"
          disabled={disabled}
          activePopup={activePopup}
          onHoverEnter={handleHoverZoneEnter}
          onHoverLeave={handleHoverZoneLeave}
          renderPopup={renderPopup}
        />

        {/* Video Icon */}
        <IconComponent
          type="camera"
          icon={Video}
          title="Video options"
          disabled={disabled}
          activePopup={activePopup}
          onHoverEnter={handleHoverZoneEnter}
          onHoverLeave={handleHoverZoneLeave}
          renderPopup={renderPopup}
        />

        {/* Attachment Icon */}
        <IconComponent
          type="attachment"
          icon={Paperclip}
          title="Attachment options"
          disabled={disabled}
          activePopup={activePopup}
          onHoverEnter={handleHoverZoneEnter}
          onHoverLeave={handleHoverZoneLeave}
          renderPopup={renderPopup}
        />
      </div>
    </div>
  );
}
